import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../api/api";
import {
  History,
  X,
  Search,
  User,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  Building2,
  List,
  GitCommit,
  CheckCircle2,
  UserCheck,
  Zap,
  Mail,
} from "lucide-react";

const THEME = "rgb(37, 99, 235)";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
}

function getRelativeTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return "";
  } catch {
    return "";
  }
}

function formatClockTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

function formatTimelineTime(dateStr) {
  const clock = formatClockTime(dateStr);
  const rel = getRelativeTime(dateStr);
  if (clock && rel) return `${clock} · ${rel}`;
  return clock || rel || formatDate(dateStr);
}

function getActorDetails(row) {
  const code = String(row?.actionType || "").toUpperCase();

  const isQueueTransferred =
    code === "MANUAL_TRANSFERRED" ||
    code === "QUEUE_TRANSFERRED" ||
    code.includes("TRANSFERRED");

  const isAutoAssignment =
    code.includes("AUTO") ||
    code === "INITIAL_AUTO_ASSIGNMENT" ||
    code === "AUTO_ASSIGNMENT";

  const isManualAssignment =
    (!isQueueTransferred && code.includes("MANUAL")) ||
    code.includes("CREATION") ||
    code === "ASSIGNMENT_CREATED" ||
    code === "MANUAL_ASSIGNMENT";

  const isStageVerifiedReassigned =
    code.includes("STAGE_VERIFIED_REASSIGNED") ||
    (code.includes("STAGE_VERIFIED") && code.includes("REASSIGN"));

  const isStageVerified =
    code.includes("STAGE_VERIFIED");

  const isEmail =
    code === "FIRST_EMAIL_RECEIVED" ||
    code === "EMAIL_RECEIVED" ||
    row?.eventType === "EMAIL";

  const isWorkItem =
    code === "WORK_ITEM_CREATED" ||
    row?.eventType === "WORK_ITEM";

  const isAssigneeChanged =
    code === "ASSIGNEE_CHANGED" ||
    code === "ASSIGNEE_CHANGE" ||
    code.includes("ASSIGNEE");

  const isRequestClosed =
    code === "REQUEST_CLOSED" ||
    code === "CLOSED" ||
    code.includes("CLOSED");

  if (isRequestClosed) {
    return {
      label: "Closed By:",
      value: row?.movedBy || row?.createdBy || "User",
    };
  }

  if (isQueueTransferred) {
    return {
      label: "Transferred By:",
      value: row?.movedBy || row?.createdBy || "User",
    };
  }

  if (isAssigneeChanged) {
    return {
      label: "By:",
      value: row?.movedBy || row?.createdBy || "User",
    };
  }

  if (isAutoAssignment) {
    return {
      label: "Created By:",
      value: "System / Classification Engine",
    };
  }

  if (isManualAssignment) {
    return {
      label: "Created By:",
      value: row?.movedBy || row?.createdBy || "User",
    };
  }

  if (isStageVerifiedReassigned) {
    return {
      label: "Verified By:",
      value: row?.movedBy || row?.createdBy || "User",
      extraLabel: "Assigned By:",
      extraValue: "System / Classification Engine",
    };
  }

  if (isStageVerified) {
    return {
      label: "Verified By:",
      value: row?.movedBy || row?.createdBy || "User",
    };
  }

  if (isEmail) {
    return {
      label: "Received By:",
      value: row?.movedBy || row?.createdBy || "Email Gateway",
    };
  }

  if (isWorkItem) {
    return {
      label: "Created By:",
      value: row?.movedBy || row?.createdBy || "System",
    };
  }

  return {
    label: "By:",
    value: row?.movedBy || row?.createdBy || "System",
  };
}


function ActionBadge({ actionType }) {
  const code = String(actionType || "").toUpperCase();
  const tone = EVENT_TONES[getEventTone({ actionType })] || EVENT_TONES.slate;

  let label = "Event";
  if (code === "REQUEST_CLOSED" || code === "CLOSED" || code.includes("CLOSED")) {
    label = "Request Closed";
  } else if (code === "FIRST_EMAIL_RECEIVED") {
    label = "First Email Received";
  } else if (code === "EMAIL_RECEIVED") {
    label = "Email Received";
  } else if (code === "WORK_ITEM_CREATED") {
    label = "Work Item Created";
  } else if (code === "STAGE_VERIFIED_REASSIGNED" || code.includes("STAGE_VERIFIED_REASSIGNED")) {
    label = "Stage Verified & Re-assigned";
  } else if (code === "MANUAL_TRANSFERRED" || code === "QUEUE_TRANSFERRED" || code.includes("TRANSFERRED")) {
    label = "Queue Transferred";
  } else if (code === "AUTO_REASSIGNED" || code === "RE_ASSIGNMENT") {
    label = "Auto Reassigned";
  } else if (code === "ASSIGNEE_CHANGED" || code === "ASSIGNEE_CHANGE" || code.includes("ASSIGNEE")) {
    label = "Assignee Changed";
  } else if (actionType) {
    label = actionType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: 0,
        fontSize: "12px",
        fontWeight: "600",
        background: "transparent",
        color: tone.iconColor,
        whiteSpace: "nowrap",
      }}
    >
      <EventTypeIcon row={{ actionType }} size={13} />
      {label}
    </span>
  );
}

function dateGroupLabel(dateStr) {
  if (!dateStr) return "Unknown date";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Unknown date";
  }
}

function eventKey(row, idx) {
  if (row?.movementId != null) return String(row.movementId);
  return `event-${idx}`;
}

function isEmailEvent(row) {
  const code = String(row?.actionType || "").toUpperCase();
  return code === "FIRST_EMAIL_RECEIVED" || code === "EMAIL_RECEIVED" || row?.eventType === "EMAIL";
}

function isWorkItemEvent(row) {
  const code = String(row?.actionType || "").toUpperCase();
  return code === "WORK_ITEM_CREATED" || row?.eventType === "WORK_ITEM";
}

function getEventTone(row) {
  const code = String(row?.actionType || "").toUpperCase();
  if (code.includes("CLOSED")) return "green";
  if (isEmailEvent(row) || code.includes("EMAIL")) return "teal";
  if (isWorkItemEvent(row) || code.includes("WORK_ITEM")) return "purple";
  if (code.includes("STAGE_VERIFIED") && code.includes("REASSIGN")) return "orange";
  if (code.includes("REASSIGN")) return "orange";
  if (code.includes("TRANSFERRED")) return "blue";
  if (code.includes("VERIFIED") || code.includes("STAGE")) return "green";
  if (code.includes("CREATION") || code.includes("MANUAL")) return "blue";
  if (code.includes("TRANSITION")) return "green";
  return "slate";
}

const EVENT_TONES = {
  teal: {
    stripe: "#2dd4bf",
    iconBg: "#f0fdfa",
    iconColor: "#0d9488",
    selectedBg: "rgba(240, 253, 250, 0.9)",
    hoverBg: "rgba(240, 253, 250, 0.55)",
  },
  purple: {
    stripe: "#a78bfa",
    iconBg: "#f5f3ff",
    iconColor: "#7c3aed",
    selectedBg: "rgba(245, 243, 255, 0.9)",
    hoverBg: "rgba(245, 243, 255, 0.55)",
  },
  orange: {
    stripe: "#fbbf24",
    iconBg: "#fffbeb",
    iconColor: "#d97706",
    selectedBg: "rgba(255, 251, 235, 0.9)",
    hoverBg: "rgba(255, 251, 235, 0.55)",
  },
  green: {
    stripe: "#34d399",
    iconBg: "#ecfdf5",
    iconColor: "#059669",
    selectedBg: "rgba(236, 253, 245, 0.9)",
    hoverBg: "rgba(236, 253, 245, 0.55)",
  },
  blue: {
    stripe: "#60a5fa",
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    selectedBg: "rgba(239, 246, 255, 0.9)",
    hoverBg: "rgba(239, 246, 255, 0.55)",
  },
  slate: {
    stripe: "#94a3b8",
    iconBg: "#f8fafc",
    iconColor: "#475569",
    selectedBg: "#f8fafc",
    hoverBg: "#f1f5f9",
  },
};

function EventTypeIcon({ row, size = 13 }) {
  const code = String(row?.actionType || "").toUpperCase();
  if (code.includes("CLOSED")) return <CheckCircle2 size={size} />;
  if (code.includes("EMAIL")) return <Mail size={size} />;
  if (code.includes("WORK_ITEM") || code.includes("CREATION") || code.includes("AUTO")) return <Zap size={size} />;
  if (code.includes("REASSIGN")) return <UserCheck size={size} />;
  if (code.includes("VERIFIED") || code.includes("STAGE") || code.includes("TRANSITION")) return <CheckCircle2 size={size} />;
  return <GitCommit size={size} />;
}

function displayName(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const angled = text.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (angled) return angled[1].trim() || angled[2];
  const lower = text.toLowerCase();
  if (lower.includes("system") || lower.includes("engine") || lower.includes("gateway")) return text;
  const at = text.indexOf("@");
  if (at > 0 && !text.includes(" ")) return text.slice(0, at);
  return text;
}

function getEventSummary(row) {
  const code = String(row?.actionType || "").toUpperCase();
  const assignee = displayName(row.assignedTo);
  const actor = displayName(row.movedBy || row.createdBy);

  if (code.includes("CLOSED")) {
    const actorStr = actor ? `By ${actor}` : "";
    return [row.remarks || "Request closed in Work Pool & Azure DevOps", actorStr].filter(Boolean).join(" · ");
  }

  if (isEmailEvent(row)) {
    const subject = row.emailDetails?.emailSubject || "Email received";
    const from = row.movedBy || row.createdBy || row.emailDetails?.from || "";
    return [subject, from].filter(Boolean).join(" · ");
  }

  if (isWorkItemEvent(row)) {
    const parts = [];
    if (row.witsSerialNumber) parts.push(`${row.witsSerialNumber} created`);
    if (row.documentType) parts.push(`Doc type ${row.documentType}`);
    if (actor) parts.push(`By ${actor}`);
    return parts.join(" · ") || "Work item created";
  }

  if (code.includes("STAGE_VERIFIED") && code.includes("REASSIGN")) {
    const moved = row.previousDepartment
      ? `Moved from ${row.previousDepartment} → ${row.currentDepartment || "N/A"}`
      : (row.currentDepartment ? `Department ${row.currentDepartment}` : "");
    const doc = row.documentType ? `Doc type ${row.documentType}` : "";
    const reassigned = assignee ? `Re-assigned to ${assignee}` : "";
    return [moved, doc, reassigned].filter(Boolean).join(" · ") || "Stage verified & re-assigned";
  }

  if (code.includes("VERIFIED") || code.includes("STAGE") || code.includes("TRANSITION")) {
    const prev = row.previousDepartment;
    const curr = row.currentDepartment;
    const dept = prev && curr
      ? (prev === curr ? `Dept stayed ${curr}` : `Moved from ${prev} → ${curr}`)
      : (curr ? `Dept stayed ${curr}` : "");
    const prevDoc = row.previousDocumentType;
    const currDoc = row.documentType;
    const doc = prevDoc && currDoc && prevDoc !== currDoc
      ? `Doc changed ${prevDoc} → ${currDoc}`
      : (currDoc ? `Doc type ${currDoc}` : "");
    const assigned = assignee ? `Assigned to ${assignee}` : "";
    return [dept, doc, assigned].filter(Boolean).join(" · ") || "Stage update";
  }

  if (code === "MANUAL_TRANSFERRED" || code === "QUEUE_TRANSFERRED" || code.includes("TRANSFERRED")) {
    const prev = row.previousDepartment;
    const curr = row.currentDepartment;
    const deptStr = (prev && curr && prev !== curr)
      ? `Transferred ${prev} → ${curr}`
      : `Queued to ${curr || "Department"}`;
    const assigned = assignee ? `Assigned to ${assignee}` : "";
    const by = actor ? `By ${actor}` : "";
    return [deptStr, assigned, by].filter(Boolean).join(" · ");
  }

  if (code === "AUTO_REASSIGNED" || (code.includes("REASSIGN") && code.includes("AUTO")) || code.includes("REASSIGN")) {
    const prev = row.previousDepartment;
    const curr = row.currentDepartment;
    let deptStr = "";
    if (prev && curr && prev !== curr) {
      deptStr = `${prev} → ${curr}`;
    }
    const assignedStr = assignee
      ? (assignee === "Unassigned" ? "Assigned to Unassigned" : `Assigned to ${assignee}`)
      : "";
    return [deptStr, assignedStr].filter(Boolean).join(" · ") || "Auto Reassigned";
  }

  if (code === "ASSIGNEE_CHANGED" || code === "ASSIGNEE_CHANGE" || code.includes("ASSIGNEE")) {
    const assignedStr = assignee
      ? (assignee === "Unassigned" ? "Assigned to Unassigned" : `Assigned to ${assignee}`)
      : "Assigned to Unassigned";
    const byStr = actor ? `By ${actor}` : "";
    return [assignedStr, byStr].filter(Boolean).join(" · ");
  }

  if (code.includes("CREATION") || code === "MANUAL_CREATION") {
    const dept = row.currentDepartment
      ? `Created in ${row.currentDepartment}${row.previousDepartment ? "" : " (Initial)"}`
      : "Created";
    const assigned = assignee ? `Assigned to ${assignee}` : "";
    const by = actor ? `By ${actor}` : "";
    return [dept, assigned, by].filter(Boolean).join(" · ");
  }

  const prev = row.previousDepartment;
  const curr = row.currentDepartment;
  const dept = (prev && curr && prev !== curr)
    ? `${prev} → ${curr}`
    : (curr || "");
  const assigned = assignee ? `Assigned to ${assignee}` : "";
  return [dept, assigned].filter(Boolean).join(" · ") || "Event";
}

function groupHistoryByDate(items) {
  const groups = [];
  items.forEach((row, idx) => {
    const date = dateGroupLabel(row.movedAt || row.createdAt);
    const last = groups[groups.length - 1];
    const entry = { row, idx, key: eventKey(row, idx) };
    if (last && last.date === date) last.items.push(entry);
    else groups.push({ date, items: [entry] });
  });
  return groups;
}

function DrawerField({ label, children }) {
  const text = String(label || "").replace(/:$/, "");
  return (
    <div style={s.drawerField}>
      <p style={s.drawerFieldLabel}>{text}</p>
      <div>{children}</div>
    </div>
  );
}

function DrawerPills({ parts, variant = "violet" }) {
  const items = (Array.isArray(parts) ? parts : [parts]).filter(Boolean);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
      {items.map((part, i) => (
        <span key={`${part}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={variant === "amber" ? s.drawerDocPill : s.drawerDeptPill}>{part}</span>
          {i < items.length - 1 ? <ArrowRight size={12} color="#d1d5db" /> : null}
        </span>
      ))}
    </div>
  );
}

function EventDetailsFields({ row }) {
  const actor = getActorDetails(row);

  if (isEmailEvent(row)) {
    return (
      <>
        {row.documentType ? (
          <DrawerField label="Doc Type">
            <DrawerPills parts={[row.documentType]} variant="violet" />
          </DrawerField>
        ) : null}
        <DrawerField label="Email Subject">
          <p style={s.drawerPlainValue}>
            {row.emailDetails?.emailSubject || row.remarks || "No Subject"}
          </p>
        </DrawerField>
        <DrawerField label={actor.label}>
          <p style={s.drawerPlainValue}>{actor.value}</p>
        </DrawerField>
      </>
    );
  }

  if (isWorkItemEvent(row)) {
    return (
      <>
        {row.witsSerialNumber ? (
          <DrawerField label="Work Item ID">
            <span style={s.serialBadge}>{row.witsSerialNumber}</span>
          </DrawerField>
        ) : null}
        {row.documentType ? (
          <DrawerField label="Doc Type">
            <DrawerPills parts={[row.documentType]} variant="violet" />
          </DrawerField>
        ) : null}
        <DrawerField label={actor.label}>
          <p style={s.drawerPlainValue}>{actor.value}</p>
        </DrawerField>
      </>
    );
  }

  const deptParts = (row.previousDepartment && row.previousDepartment !== row.currentDepartment)
    ? [row.previousDepartment, row.currentDepartment]
    : [row.currentDepartment || "N/A"];
  const docChanged = row.previousDocumentType && row.documentType && row.previousDocumentType !== row.documentType;
  const docParts = docChanged
    ? [row.previousDocumentType, row.documentType]
    : (row.documentType || row.previousDocumentType ? [row.documentType || row.previousDocumentType] : []);

  return (
    <>
      <DrawerField label="Department">
        <DrawerPills parts={deptParts} variant="violet" />
      </DrawerField>

      {docParts.length ? (
        <DrawerField label="Doc Type">
          <DrawerPills parts={docParts} variant={docChanged ? "amber" : "violet"} />
        </DrawerField>
      ) : null}

      <DrawerField label="Assigned To">
        <p style={s.drawerPlainValue}>{row.assignedTo || "Unassigned"}</p>
      </DrawerField>

      <DrawerField label={actor.label}>
        <p style={s.drawerPlainValue}>{actor.value}</p>
      </DrawerField>

      {actor.extraLabel ? (
        <DrawerField label={actor.extraLabel}>
          <p style={s.drawerPlainValue}>{actor.extraValue}</p>
        </DrawerField>
      ) : null}
    </>
  );
}

const DRAWER_MS = 280;
const DRAWER_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

function EventDrawer({ event, onClose }) {
  const [displayed, setDisplayed] = useState(event);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const replayTimer = useRef(null);
  const openRef = useRef(false);
  const shownKeyRef = useRef(event ? eventKey(event, 0) : null);
  const pendingRef = useRef(event);
  const row = displayed;

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const clearMotionTimers = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (replayTimer.current) {
      window.clearTimeout(replayTimer.current);
      replayTimer.current = null;
    }
  };

  const slideIn = (next) => {
    shownKeyRef.current = next ? eventKey(next, 0) : null;
    setDisplayed(next);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setOpen(true));
    });
  };

  const requestClose = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => {
      onClose();
    }, DRAWER_MS);
  }, [onClose]);

  useEffect(() => {
    pendingRef.current = event;
    clearMotionTimers();

    if (!event) {
      setOpen(false);
      closeTimer.current = window.setTimeout(() => {
        setDisplayed(null);
        shownKeyRef.current = null;
        closeTimer.current = null;
      }, DRAWER_MS);
      return clearMotionTimers;
    }

    const nextKey = eventKey(event, 0);
    const switching = openRef.current && shownKeyRef.current && shownKeyRef.current !== nextKey;

    if (switching) {
      setOpen(false);
      replayTimer.current = window.setTimeout(() => {
        const latest = pendingRef.current;
        replayTimer.current = null;
        if (latest) slideIn(latest);
      }, DRAWER_MS);
      return clearMotionTimers;
    }

    slideIn(event);
    return clearMotionTimers;
  }, [event]);

  useEffect(() => {
    if (!row) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [row, requestClose]);

  if (!row) return null;

  const tone = EVENT_TONES[getEventTone(row)] || EVENT_TONES.slate;

  return (
    <aside
      style={{
        ...s.drawerPanel,
        transform: open ? "translate3d(0, 0, 0)" : "translate3d(100%, 0, 0)",
        boxShadow: open ? "-16px 0 48px rgba(15, 23, 42, 0.18)" : "none",
        pointerEvents: open ? "auto" : "none",
      }}
      role="dialog"
      aria-label="Event details"
      aria-hidden={!event && !open}
    >
      <div style={{ ...s.drawerStripe, background: tone.stripe }} />
      <div style={s.drawerHeader}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span
              style={{
                ...s.logIcon,
                background: tone.iconBg,
                color: tone.iconColor,
              }}
            >
              <EventTypeIcon row={row} />
            </span>
            <ActionBadge actionType={row.actionType} />
          </div>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            {formatDate(row.movedAt || row.createdAt)}
          </p>
        </div>
        <button type="button" onClick={requestClose} style={s.closeBtn} title="Close details">
          <X size={16} />
        </button>
      </div>
      <div style={s.drawerBody}>
        <p style={s.drawerSectionLabel}>Event Details</p>
        <div style={s.timelineBody}>
          <EventDetailsFields row={row} />
        </div>
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "20px", marginTop: "8px" }}>
          <p style={s.drawerSectionLabel}>Note</p>
          {row.remarks && !isEmailEvent(row) ? (
            <div style={s.drawerNoteBox}>
              <p style={s.drawerNoteText}>{row.remarks}</p>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: "12px", color: "#d1d5db", fontStyle: "italic" }}>
              No note attached to this event.
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}

function HistoryLegend() {
  const items = [
    { tone: "green", label: "Stage Verified" },
    { tone: "orange", label: "Re-assigned" },
    { tone: "blue", label: "Manual" },
    { tone: "purple", label: "System" },
    { tone: "teal", label: "Email" },
  ];
  return (
    <div style={s.legendRow}>
      {items.map((item) => (
        <span key={item.tone} style={s.legendItem}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: EVENT_TONES[item.tone].stripe,
              flexShrink: 0,
            }}
          />
          {item.label}
        </span>
      ))}
      <span style={s.legendItem}>
        <MessageSquare size={11} /> Has note
      </span>
    </div>
  );
}

function HistoryToolbar({
  currentDept,
  currentAssignee,
  totalEvents,
  searchQuery,
  setSearchQuery,
  viewMode,
  setViewMode,
  loadHistory,
  loading,
}) {
  const assignee = formatPersonLines(currentAssignee);
  const assigneeEmail = assignee.secondary
    || (String(currentAssignee || "").match(/\s([^\s]+@[^\s]+)\s*$/) || [])[1]
    || "";
  const assigneeName = assignee.secondary
    ? assignee.primary
    : (assigneeEmail
      ? String(currentAssignee).replace(/\s+[^\s]+@[^\s]+\s*$/, "").trim() || assignee.primary
      : assignee.primary);

  return (
    <>
      <div style={s.metaRow}>
        <div style={s.statBlock}>
          <Building2 size={14} color="#14b8a6" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <p style={s.contextLabel}>Current Department</p>
            <p style={s.contextValue} title={currentDept}>{currentDept}</p>
          </div>
        </div>
        <div style={s.statBlock}>
          <User size={14} color="#14b8a6" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <p style={s.contextLabel}>Current Assignee</p>
            <p style={s.contextValue} title={currentAssignee}>{assigneeName}</p>
            {assigneeEmail && assigneeEmail !== assigneeName ? (
              <p style={s.metaSecondary}>{assigneeEmail}</p>
            ) : null}
          </div>
        </div>
        <div style={s.statBlock}>
          <List size={14} color="#14b8a6" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <p style={s.contextLabel}>Total Events</p>
            <p style={s.contextValue}>{String(totalEvents)}</p>
          </div>
        </div>

        <div style={s.controlsRight}>
          <div style={s.toggleGroup}>
            <button
              type="button"
              onClick={() => setViewMode("timeline")}
              style={{
                ...s.toggleBtn,
                background: viewMode === "timeline" ? "#f5f3ff" : "#ffffff",
                color: viewMode === "timeline" ? "#7c3aed" : "#9ca3af",
                fontWeight: viewMode === "timeline" ? "600" : "500",
              }}
            >
              <GitCommit size={12} />
              Timeline
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              style={{
                ...s.toggleBtn,
                ...s.toggleBtnSplit,
                background: viewMode === "table" ? "#f5f3ff" : "#ffffff",
                color: viewMode === "table" ? "#7c3aed" : "#9ca3af",
                fontWeight: viewMode === "table" ? "600" : "500",
              }}
            >
              <List size={12} />
              Table
            </button>
          </div>

          <button
            type="button"
            onClick={loadHistory}
            style={s.refreshBtn}
            title="Refresh history records"
            disabled={loading}
          >
            <RefreshCw size={13} className={loading ? "spin" : ""} />
          </button>
        </div>
      </div>

      <div style={s.controlsRow}>
        <div style={s.searchWrap}>
          <Search size={13} color="#d1d5db" />
          <input
            type="text"
            placeholder="Filter history…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={s.searchInput}
          />
          {searchQuery ? (
            <X size={12} color="#9ca3af" style={{ cursor: "pointer" }} onClick={() => setSearchQuery("")} />
          ) : null}
        </div>
        <HistoryLegend />
      </div>
    </>
  );
}

function rowFill(selected, hovered) {
  if (selected) return "#f1f5f9";
  if (hovered) return "#f8fafc";
  return "#ffffff";
}

function TimelineLog({ events, selectedKey, onSelect }) {
  const [hoveredKey, setHoveredKey] = useState(null);
  const groups = groupHistoryByDate(events);

  return (
    <div style={s.logWrap}>
      {groups.map((group) => (
        <div key={group.date}>
          <div style={s.dateHeader}>
            <span style={s.dateHeaderLabel}>{group.date}</span>
            <span style={s.dateRule} />
            <span style={s.dateHeaderCount}>
              {group.items.length} event{group.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div style={s.logList}>
            {group.items.map(({ row, key }) => {
              const tone = EVENT_TONES[getEventTone(row)] || EVENT_TONES.slate;
              const selected = selectedKey === key;
              const hovered = hoveredKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelect(key)}
                  onMouseEnter={() => setHoveredKey(key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  style={{
                    ...s.logRow,
                    borderLeftColor: selected || hovered ? tone.stripe : "transparent",
                    background: rowFill(selected, hovered),
                  }}
                >
                  <span style={s.logText}>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <ActionBadge actionType={row.actionType} />
                      {row.remarks && !isEmailEvent(row) ? (
                        <MessageSquare size={12} color="#64748b" title="Has note" />
                      ) : null}
                    </span>
                    <span style={s.logSummary}>{getEventSummary(row)}</span>
                  </span>
                  <span style={s.logTime}>{formatTimelineTime(row.movedAt || row.createdAt)}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatPersonLines(value) {
  const text = String(value || "").trim();
  if (!text || text === "—") return { primary: "—", secondary: "" };
  const angled = text.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (angled) {
    return { primary: angled[1].trim() || angled[2], secondary: angled[2] };
  }
  return { primary: text, secondary: "" };
}

function PersonCell({ value }) {
  const { primary, secondary } = formatPersonLines(value);
  if (primary === "—") {
    return <span style={s.nullDash}>—</span>;
  }
  return (
    <span style={s.tblPerson}>
      <span style={s.tblPersonName}>{primary}</span>
      {secondary ? <span style={s.tblPersonEmail}>{secondary}</span> : null}
    </span>
  );
}

function HistoryTable({ events, selectedKey, onSelect }) {
  const [hoveredKey, setHoveredKey] = useState(null);
  const groups = groupHistoryByDate(events);

  return (
    <div style={s.tableContainer}>
      <table style={s.tblGrid}>
        <thead>
          <tr style={s.thRow}>
            <th style={s.thRail} aria-hidden="true" />
            <th style={{ ...s.th, width: "16%" }}>Date & Time</th>
            <th style={{ ...s.th, width: "12%" }}>Move From</th>
            <th style={{ ...s.th, width: "12%" }}>Move To</th>
            <th style={{ ...s.th, width: "20%" }}>Assignee To</th>
            <th style={{ ...s.th, width: "18%" }}>By</th>
            <th style={{ ...s.th, width: "22%" }}>Comment / Action</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.date}>
              <tr>
                <td colSpan={7} style={s.tblGroupCell}>
                  <div style={s.dateHeader}>
                    <span style={s.dateHeaderLabel}>{group.date}</span>
                    <span style={s.dateRule} />
                    <span style={s.dateHeaderCount}>
                      {group.items.length} event{group.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </td>
              </tr>
              {group.items.map(({ row, key }) => {
                const tone = EVENT_TONES[getEventTone(row)] || EVENT_TONES.slate;
                const selected = selectedKey === key;
                const hovered = hoveredKey === key;
                const rel = getRelativeTime(row.movedAt || row.createdAt);
                const skipMove = isEmailEvent(row) || isWorkItemEvent(row);
                const actor = getActorDetails(row);
                const commentText = skipMove
                  ? null
                  : row.remarks || null;
                const actionDetail = isEmailEvent(row)
                  ? (row.emailDetails?.emailSubject || row.remarks || "No Subject")
                  : isWorkItemEvent(row)
                    ? (row.witsSerialNumber ? `Work Item ID: ${row.witsSerialNumber}` : "Work Item Generated")
                    : null;

                return (
                  <tr
                    key={key}
                    onClick={() => onSelect(key)}
                    onMouseEnter={() => setHoveredKey(key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    style={{
                      ...s.tr,
                      cursor: "pointer",
                      background: rowFill(selected, hovered),
                    }}
                  >
                    <td
                      style={{
                        ...s.tblRail,
                        background: selected || hovered ? tone.stripe : "transparent",
                        opacity: selected ? 1 : hovered ? 0.45 : 1,
                      }}
                    />
                    <td style={s.tdDate}>
                      <span style={s.tblDatePrimary}>{formatDate(row.movedAt || row.createdAt)}</span>
                      {rel ? <span style={s.dateSub}>{rel}</span> : null}
                    </td>
                    <td style={s.td}>
                      {skipMove ? (
                        <span style={s.nullDash}>—</span>
                      ) : row.previousDepartment ? (
                        <span style={s.deptPillPrev}>{row.previousDepartment}</span>
                      ) : (
                        <span style={s.tblInitialPill}>— (Initial)</span>
                      )}
                    </td>
                    <td style={s.td}>
                      {skipMove ? (
                        <span style={s.nullDash}>—</span>
                      ) : (
                        <span style={s.tblMoveTo}>
                          <ArrowRight size={10} />
                          {row.currentDepartment || "N/A"}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      {skipMove ? (
                        <span style={s.nullDash}>—</span>
                      ) : (
                        <PersonCell value={row.assignedTo || "Unassigned"} />
                      )}
                    </td>
                    <td style={s.td}>
                      <PersonCell value={actor.value} />
                    </td>
                    <td style={s.tdRemarks}>
                      <div style={s.tblActionHead}>
                        <ActionBadge actionType={row.actionType} />
                        {commentText ? <MessageSquare size={12} color="#64748b" title="Has note" /> : null}
                      </div>
                      {actionDetail ? (
                        <span style={s.tblComment}>{actionDetail}</span>
                      ) : commentText ? (
                        <span style={s.tblComment}>
                          {commentText}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RequestHistoryModal({ isOpen = true, onClose, workItem, isFullPage = false, isWorkPool = false }) {
  const { instance, accounts } = useMsal();
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("timeline"); // 'timeline' | 'table'
  const [selectedId, setSelectedId] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!workItem) return;
    try {
      setLoading(true);
      setError("");

      const witsSerial = workItem.workItemNum || workItem.witsSerialNumber || workItem.wits_serial_number || "";
      const adoId = workItem.id || workItem.adoUserStoryId || workItem.ado_user_story_id || "";
      const msgId = workItem.msg_id || workItem.msgId || "";
      const convId = workItem.conversation_id || workItem.conversationId || "";

      const checkWorkPool = isWorkPool || Boolean(convId) || workItem.isWorkPool;

      let queryParams = [];
      if (witsSerial) queryParams.push(`witsSerialNumber=${encodeURIComponent(witsSerial)}`);
      if (adoId) queryParams.push(`adoUserStoryId=${encodeURIComponent(adoId)}`);
      if (msgId) queryParams.push(`msgId=${encodeURIComponent(msgId)}`);
      if (convId) queryParams.push(`conversationId=${encodeURIComponent(convId)}`);

      const endpoint = checkWorkPool ? "/api/departments/work-pool-history" : "/api/departments/movements";
      const url = `${endpoint}?${queryParams.join("&")}&limit=100&_t=${Date.now()}`;
      const res = await callApi(instance, accounts, "GET", url);

      const items = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

      setHistoryList(items);
    } catch (err) {
      console.error("Failed to load movement history:", err);
      setError(err?.response?.data?.message || "Failed to load request movement history.");
    } finally {
      setLoading(false);
    }
  }, [workItem, isWorkPool, instance, accounts]);


  useEffect(() => {
    if (isOpen) {
      loadHistory();
    } else {
      setHistoryList([]);
      setSearchQuery("");
      setError("");
      setSelectedId(null);
    }
  }, [isOpen, loadHistory]);

  if (!isOpen || !workItem) return null;

  const filteredHistory = historyList.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (item.previousDepartment && item.previousDepartment.toLowerCase().includes(q)) ||
      (item.currentDepartment && item.currentDepartment.toLowerCase().includes(q)) ||
      (item.assignedTo && item.assignedTo.toLowerCase().includes(q)) ||
      (item.movedBy && item.movedBy.toLowerCase().includes(q)) ||
      (item.actionType && item.actionType.toLowerCase().includes(q)) ||
      (item.remarks && item.remarks.toLowerCase().includes(q)) ||
      (item.documentType && item.documentType.toLowerCase().includes(q)) ||
      (item.previousDocumentType && item.previousDocumentType.toLowerCase().includes(q)) ||
      (item.witsSerialNumber && item.witsSerialNumber.toLowerCase().includes(q)) ||
      (item.emailDetails?.emailSubject && item.emailDetails.emailSubject.toLowerCase().includes(q))
    );
  });

  const checkWorkPool = isWorkPool || workItem?.isWorkPool || Boolean(workItem?.conversation_id);

  const serialNum = workItem.workItemNum || workItem.witsSerialNumber || workItem.wits_serial_number || "N/A";
  const adoId = workItem.id || workItem.adoUserStoryId || workItem.ado_user_story_id || workItem.user_story_id || "N/A";
  const org = workItem.organization || workItem.ado_organization || "DWS10062665";
  const project = workItem.project || workItem.ado_project || "ADLS Migration";
  const adoLink = adoId && adoId !== "N/A"
    ? `https://dev.azure.com/${encodeURIComponent(org)}/${encodeURIComponent(project)}/_workitems/edit/${adoId}`
    : null;

  const latestMove = historyList[0];
  const currentDept = latestMove?.currentDepartment || workItem.department || (checkWorkPool ? "Work Pool" : "N/A");
  const currentAssignee = latestMove?.assignedTo || workItem.assignedTo || "Unassigned";
  const backLabel = checkWorkPool ? "Back to Work Pool" : "Back to Request Pool";
  const selectedEvent = filteredHistory.find((row, idx) => eventKey(row, idx) === selectedId) || null;
  const handleSelectEvent = (key) => {
    setSelectedId(key);
  };
  const toolbarProps = {
    currentDept,
    currentAssignee,
    totalEvents: historyList.length,
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode,
    loadHistory,
    loading,
  };

  if (isFullPage) {
    return (
      <div style={{ width: "100%", padding: "0 0 32px 0", boxSizing: "border-box" }}>
        {/* BREADCRUMB HEADER */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ fontSize: "12.5px", color: "#64748b", fontWeight: "500", display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
            <span style={{ cursor: "pointer", color: "#64748b" }} onClick={onClose}>
              {checkWorkPool ? "Administration" : "Document Index Hub"}
            </span>
            <span>&gt;</span>
            <span
              style={{ cursor: "pointer", color: "#2563eb" }}
              onClick={onClose}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              {checkWorkPool ? "Work Pool" : "Request Pool"}
            </span>
            <span>&gt;</span>
            <span style={{ color: "#0f172a", fontWeight: "700" }}>{checkWorkPool ? "Work Pool History" : "Request History"}</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#0f172a", margin: 0, letterSpacing: "-0.02em" }}>
                  {checkWorkPool ? "Work Pool History" : "Request History"}
                </h1>
                {serialNum && serialNum !== "N/A" && serialNum !== "—" && (
                  <span style={s.serialBadge}>{serialNum}</span>
                )}
                {adoId !== "N/A" && (
                  adoLink ? (
                    <a
                      href={adoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.adoBadge}
                      title={`Open User Story ${adoId} in Azure DevOps`}
                    >
                      <span>User Story {adoId}</span>
                      <ExternalLink size={11} style={{ flexShrink: 0 }} />
                    </a>
                  ) : (
                    <span style={s.adoBadge}>
                      User Story {adoId}
                    </span>
                  )
                )}
              </div>
              <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0 0" }}>
                {workItem.title || workItem.latest_email_subject ? (workItem.title || workItem.latest_email_subject) : `View complete email & movement audit trail`}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
              }}
            >
              <ArrowLeft size={15} /> {backLabel}
            </button>
          </div>
        </div>

        {/* PAGE MAIN CARD */}
        <div style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.05)", overflow: "hidden" }}>
          <div style={s.toolbarWrap}>
            <HistoryToolbar {...toolbarProps} />
          </div>

          {/* ERROR ALERT */}
          {error && (
            <div style={s.errorAlert}>
              <span>{error}</span>
            </div>
          )}

          {/* CONTENT AREA */}
          <div style={s.body}>
            {loading ? (
              <div style={s.skeletonWrap}>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={s.skeletonRow}>
                    <div style={{ ...s.skeletonBar, width: "120px" }} />
                    <div style={{ ...s.skeletonBar, width: "100px" }} />
                    <div style={{ ...s.skeletonBar, width: "100px" }} />
                    <div style={{ ...s.skeletonBar, width: "130px" }} />
                    <div style={{ ...s.skeletonBar, width: "180px" }} />
                  </div>
                ))}
              </div>
            ) : filteredHistory.length === 0 ? (
              <div style={s.emptyState}>
                <History size={36} color="#cbd5e1" />
                <p style={s.emptyTitle}>No history records found</p>
                <p style={s.emptySub}>
                  {searchQuery ? "No events match your filter query." : "No movement or action history logged for this work item yet."}
                </p>
              </div>
            ) : viewMode === "table" ? (
              <HistoryTable
                events={filteredHistory}
                selectedKey={selectedId}
                onSelect={handleSelectEvent}
              />
            ) : (
              <TimelineLog
                events={filteredHistory}
                selectedKey={selectedId}
                onSelect={handleSelectEvent}
              />
            )}
          </div>

          {/* FOOTER */}
          <div style={s.footer}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              Showing <strong>{filteredHistory.length}</strong> event(s) for work item {serialNum}
            </span>
            <button type="button" onClick={onClose} style={s.closeFooterBtn}>
              {backLabel}
            </button>
          </div>
        </div>
        <EventDrawer
          event={selectedEvent}
          onClose={() => setSelectedId(null)}
        />
      </div>
    );
  }

  return (
    <div
      style={s.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={s.modalCard}>
        {/* HEADER */}
        <div style={s.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={s.iconBadge}>
              <History size={18} color="#2563eb" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h3 style={s.headerTitle}>{checkWorkPool ? "Work Pool History" : "Request History"}</h3>
                {serialNum && serialNum !== "N/A" && serialNum !== "—" && (
                  <span style={s.serialBadge}>{serialNum}</span>
                )}
                {adoId !== "N/A" && (
                  adoLink ? (
                    <a
                      href={adoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.adoBadge}
                      title={`Open User Story ${adoId} in Azure DevOps`}
                    >
                      <span>User Story {adoId}</span>
                      <ExternalLink size={11} style={{ flexShrink: 0 }} />
                    </a>
                  ) : (
                    <span style={s.adoBadge}>
                      User Story {adoId}
                    </span>
                  )
                )}
              </div>
              <p style={s.headerSub} title={workItem.title}>
                {workItem.title ? (workItem.title.length > 70 ? `${workItem.title.substring(0, 67)}...` : workItem.title) : "Work Item Audit Trail"}
              </p>
            </div>
          </div>

          <button type="button" onClick={onClose} style={s.closeBtn} title="Close History">
            <X size={18} />
          </button>
        </div>

        <div style={s.toolbarWrap}>
          <HistoryToolbar {...toolbarProps} />
        </div>

        {/* CONTENT BODY */}
        <div style={s.body}>
          {error && <div style={s.errorBox}>{error}</div>}

          {loading && historyList.length === 0 ? (
            <div style={s.skeletonWrap}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={s.skeletonRow}>
                  <div style={{ ...s.skeletonBar, width: "120px" }} />
                  <div style={{ ...s.skeletonBar, width: "100px" }} />
                  <div style={{ ...s.skeletonBar, width: "100px" }} />
                  <div style={{ ...s.skeletonBar, width: "130px" }} />
                  <div style={{ ...s.skeletonBar, width: "180px" }} />
                </div>
              ))}
            </div>
          ) : filteredHistory.length === 0 ? (
            <div style={s.emptyState}>
              <History size={36} color="#cbd5e1" />
              <p style={s.emptyTitle}>No history records found</p>
              <p style={s.emptySub}>
                {searchQuery ? "No events match your filter query." : "No movement or action history logged for this work item yet."}
              </p>
            </div>
          ) : viewMode === "table" ? (
            <HistoryTable
              events={filteredHistory}
              selectedKey={selectedId}
              onSelect={handleSelectEvent}
            />
          ) : (
            <TimelineLog
              events={filteredHistory}
              selectedKey={selectedId}
              onSelect={handleSelectEvent}
            />
          )}
        </div>

        {/* FOOTER */}
        <div style={s.footer}>
          <span style={{ fontSize: "12px", color: "#64748b" }}>
            Showing <strong>{filteredHistory.length}</strong> event(s){serialNum && serialNum !== "N/A" && serialNum !== "—" ? ` for work item ${serialNum}` : ""}
          </span>
          <button type="button" onClick={onClose} style={s.closeFooterBtn}>
            Close
          </button>
        </div>
      </div>
      <EventDrawer
        event={selectedEvent}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 999999,
    background: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modalCard: {
    background: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "960px",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    animation: "fadeInScale 0.2s ease-out",
  },
  header: {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f8fafc",
  },
  iconBadge: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    background: "#eff6ff",
    border: "1px solid #dbeafe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: "17px",
    fontWeight: "700",
    color: "#0f172a",
  },
  headerSub: {
    margin: "2px 0 0 0",
    fontSize: "12.5px",
    color: "#64748b",
  },
  serialBadge: {
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "700",
    border: "1px solid #bfdbfe",
  },
  adoBadge: {
    background: "#eff6ff",
    color: "#2563eb",
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "11.5px",
    fontWeight: "600",
    border: "1px solid #bfdbfe",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    textDecoration: "none",
    cursor: "pointer",
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s ease",
  },

  toolbarWrap: {
    padding: 0,
    background: "#ffffff",
    borderBottom: "1px solid #f3f4f6",
  },
  metaRow: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 0,
  },
  statBlock: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "14px 20px",
    flex: "1 1 0",
    minWidth: 0,
    borderRight: "1px solid #f3f4f6",
  },
  metaSecondary: {
    margin: "2px 0 0 0",
    fontSize: "12px",
    fontWeight: "400",
    color: "#9ca3af",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contextLabel: {
    margin: "0 0 2px 0",
    fontSize: "10px",
    fontWeight: "700",
    color: "#0d9488",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  contextValue: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "700",
    color: "#1f2937",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minHeight: "32px",
    flexWrap: "wrap",
    padding: "10px 16px",
    borderTop: "1px solid #f3f4f6",
    background: "rgba(249, 250, 251, 0.4)",
  },
  controlsRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
    padding: "0 16px",
    borderRight: "none",
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    padding: 0,
    minWidth: 0,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#9ca3af",
  },
  logWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  dateHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 4px 6px",
  },
  dateHeaderLabel: {
    fontSize: "10px",
    fontWeight: "800",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    whiteSpace: "nowrap",
  },
  dateRule: {
    flex: 1,
    height: "1px",
    background: "#f1f5f9",
  },
  dateHeaderCount: {
    fontSize: "10px",
    color: "#64748b",
    whiteSpace: "nowrap",
  },
  logList: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #f1f5f9",
    borderRadius: "12px",
    overflow: "hidden",
    background: "#ffffff",
  },
  logRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textAlign: "left",
    border: "none",
    borderBottom: "1px solid #f8fafc",
    borderLeft: "4px solid transparent",
    background: "#ffffff",
    padding: "10px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.18s ease, border-left-color 0.18s ease",
  },
  logIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  logText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  logSummary: {
    fontSize: "12px",
    color: "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  logTime: {
    flexShrink: 0,
    fontSize: "11.5px",
    color: "#475569",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  drawerBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1000000,
    background: "rgba(15, 23, 42, 0.16)",
    transition: `opacity ${DRAWER_MS}ms ease`,
  },
  drawerPanel: {
    position: "fixed",
    top: 0,
    right: 0,
    height: "100%",
    width: "min(380px, 100%)",
    zIndex: 1000001,
    background: "#ffffff",
    borderLeft: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    willChange: "transform",
    transition: `transform ${DRAWER_MS}ms ${DRAWER_EASE}, box-shadow ${DRAWER_MS}ms ease`,
  },
  drawerStripe: {
    height: 4,
    width: "100%",
    flexShrink: 0,
  },
  drawerHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  drawerBody: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 18px 24px",
  },
  drawerSectionLabel: {
    margin: "0 0 12px 0",
    fontSize: "10px",
    fontWeight: "700",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
  },
  drawerField: {
    marginBottom: "14px",
  },
  drawerFieldLabel: {
    margin: "0 0 6px 0",
    fontSize: "10px",
    fontWeight: "600",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  drawerPlainValue: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  drawerDeptPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: "6px",
    background: "#ede9fe",
    color: "#6d28d9",
    border: "1px solid #ddd6fe",
    fontSize: "12px",
    fontWeight: "600",
  },
  drawerDocPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: "6px",
    background: "#fef3c7",
    color: "#b45309",
    border: "1px solid #fde68a",
    fontSize: "12px",
    fontWeight: "600",
  },
  drawerNoteBox: {
    background: "#f9fafb",
    borderRadius: "8px",
    padding: "12px 16px",
    border: "1px solid #f3f4f6",
  },
  drawerNoteText: {
    margin: 0,
    fontSize: "14px",
    color: "#4b5563",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  errorAlert: {
    padding: "10px 14px",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: "13px",
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
  },
  summaryLabel: {
    color: "#64748b",
    fontWeight: "500",
  },
  summaryValue: {
    color: "#0f172a",
    fontWeight: "700",
  },

  searchWrap: {
    display: "flex",
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "0 8px",
    height: "32px",
    width: "192px",
    maxWidth: "100%",
    flexShrink: 0,
  },
  searchInput: {
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "12px",
    color: "#0f172a",
    width: "100%",
    marginLeft: "5px",
  },

  toggleGroup: {
    display: "flex",
    alignItems: "center",
    background: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    padding: 0,
    height: "32px",
    boxSizing: "border-box",
  },
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "0 12px",
    height: "32px",
    borderRadius: 0,
    border: "none",
    fontSize: "12px",
    cursor: "pointer",
    background: "#ffffff",
    transition: "all 0.15s ease",
  },
  toggleBtnSplit: {
    borderLeft: "1px solid #e5e7eb",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    color: "#9ca3af",
    cursor: "pointer",
  },

  body: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
    minHeight: "320px",
  },
  errorBox: {
    padding: "10px 14px",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "12px",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
  },
  tblGrid: {
    width: "100%",
    minWidth: "960px",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: "12.5px",
    textAlign: "left",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12.5px",
    textAlign: "left",
  },
  thRow: {
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  thRail: {
    width: "4px",
    padding: 0,
    border: "none",
  },
  tblRail: {
    width: "4px",
    padding: 0,
    border: "none",
  },
  tblGroupCell: {
    padding: "0 8px",
    background: "rgba(248, 250, 252, 0.7)",
    borderBottom: "1px solid #f1f5f9",
  },
  tblDatePrimary: {
    display: "block",
    fontWeight: "600",
    color: "#334155",
    fontSize: "12px",
  },
  tblInitialPill: {
    display: "inline-flex",
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "11.5px",
    fontWeight: "600",
    color: "#cbd5e1",
  },
  tblMoveTo: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#1d4ed8",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    maxWidth: "100%",
  },
  tblPerson: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  tblPersonName: {
    fontSize: "12.5px",
    fontWeight: "600",
    color: "#334155",
    lineHeight: 1.35,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  tblPersonEmail: {
    fontSize: "11px",
    color: "#475569",
    lineHeight: 1.35,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  tblActionHead: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "4px",
    flexWrap: "wrap",
  },
  tblTypeIcon: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  tblComment: {
    display: "block",
    overflow: "visible",
    fontSize: "12px",
    color: "#475569",
    lineHeight: 1.45,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  th: {
    padding: "10px 12px",
    fontWeight: "700",
    color: "#475569",
    fontSize: "11.5px",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.18s ease",
  },
  td: {
    padding: "12px 14px",
    verticalAlign: "top",
    overflow: "visible",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  tdDate: {
    padding: "12px 14px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  tdRemarks: {
    padding: "12px 14px",
    verticalAlign: "top",
    overflow: "visible",
    whiteSpace: "normal",
  },
  dateMain: {
    fontWeight: "600",
    color: "#0f172a",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
  },
  dateSub: {
    fontSize: "11px",
    color: "#475569",
    marginTop: "2px",
  },
  deptPillPrev: {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: "6px",
    background: "#f1f5f9",
    color: "#475569",
    fontSize: "11.5px",
    fontWeight: "600",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  deptPillCurr: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: "6px",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "11.5px",
    fontWeight: "700",
  },
  docTypePillPrev: {
    display: "inline-block",
    padding: "2px 7px",
    borderRadius: "5px",
    background: "#fef3c7",
    color: "#92400e",
    fontSize: "11px",
    fontWeight: "600",
  },
  docTypePillCurr: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 7px",
    borderRadius: "5px",
    background: "#e0e7ff",
    color: "#3730a3",
    fontSize: "11px",
    fontWeight: "700",
  },
  nullDash: {
    color: "#94a3b8",
    fontSize: "12px",
    fontStyle: "italic",
  },
  assigneeText: {
    fontWeight: "600",
    color: "#0f172a",
    fontSize: "12.5px",
  },
  byWhomText: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#475569",
  },
  remarksBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    fontSize: "12px",
    color: "#334155",
    background: "#f8fafc",
    padding: "6px 8px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    lineHeight: "1.4",
  },

  /* TIMELINE STYLES */
  timelineContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 0",
  },
  timelineItem: {
    display: "flex",
    gap: "16px",
    position: "relative",
  },
  timelineLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "24px",
    flexShrink: 0,
  },
  timelineDot: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  timelineConnector: {
    width: "2px",
    flex: 1,
    background: "#cbd5e1",
    margin: "4px 0",
  },
  timelineCard: {
    flex: 1,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px 14px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
  },
  timelineHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  timelineBody: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  tlGridRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
  },
  tlLabel: {
    fontWeight: "600",
    color: "#64748b",
    width: "90px",
    flexShrink: 0,
  },
  tlRemarksBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    padding: "8px 10px",
    background: "#f8fafc",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    marginTop: "4px",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 20px",
    textAlign: "center",
  },
  emptyTitle: {
    margin: "12px 0 4px 0",
    fontSize: "15px",
    fontWeight: "700",
    color: "#0f172a",
  },
  emptySub: {
    margin: 0,
    fontSize: "13px",
    color: "#64748b",
  },

  skeletonWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    background: "#f8fafc",
    borderRadius: "8px",
  },
  skeletonBar: {
    height: "16px",
    background: "#e2e8f0",
    borderRadius: "4px",
  },

  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f8fafc",
  },
  closeFooterBtn: {
    padding: "6px 16px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    fontWeight: "600",
    fontSize: "12.5px",
    cursor: "pointer",
  },
};
